import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, HStack, Text } from '@chakra-ui/react';
import { useIntl } from 'react-intl';
import { LuChevronRight } from 'react-icons/lu';
import { GroupSwitcher } from './GroupSwitcher';
import { HomeSwitcher } from './HomeSwitcher';
import { useGroups } from '@/contexts/GroupsContext';
import { useHomes } from '@/contexts/HomesContext';
import type { PageCrumb } from '@/contexts/PageChromeContext';
import type { Workspace } from './useWorkspaceNav';

export interface PageBreadcrumbsProps {
    workspace: Workspace | null;
    crumbs: PageCrumb[];
    /** Rendered in place of the last crumb's label — the page's title. */
    current?: ReactNode;
    /** Mobile: the switcher lives in the More menu, so only the title shows. */
    compact?: boolean;
}

export function PageBreadcrumbs({ workspace, crumbs, current, compact = false }: PageBreadcrumbsProps) {
    const intl = useIntl();
    const { homes } = useHomes();
    const { groups } = useGroups();
    const home = homes.find((entry) => entry.id === workspace?.id) ?? null;
    const group = groups.find((entry) => entry.id === workspace?.id) ?? null;

    const trail: PageCrumb[] = crumbs.length ? crumbs : [{ label: '' }];
    const shown = compact ? trail.slice(-1) : trail;

    const separator = (
        <Box color="border.emphasized" flexShrink={0} aria-hidden>
            <LuChevronRight size={12} />
        </Box>
    );

    return (
        <Box
            as="nav"
            aria-label={intl.formatMessage({
                id: 'a5f38c02-6b19-4d47-9e83-2c0a7f5b1d96',
                defaultMessage: 'Breadcrumb',
            })}
            minW={0}
        >
            <HStack as="ol" gap="6px" listStyleType="none" minW={0}>
                {workspace && !compact && (
                    <Box as="li" display="flex" alignItems="center" minW={0}>
                        {workspace.kind === 'home' ? (
                            <HomeSwitcher home={home} variant="crumb" />
                        ) : (
                            <GroupSwitcher group={group} variant="crumb" />
                        )}
                    </Box>
                )}

                {shown.map((crumb, index) => {
                    const last = index === shown.length - 1;
                    const leading = index > 0 || (workspace && !compact);
                    return (
                        <HStack as="li" key={`${crumb.label}-${index}`} gap="6px" minW={0}>
                            {leading && separator}
                            {last ? (
                                <Text
                                    as="h1"
                                    fontSize={compact ? 'md' : 'sm'}
                                    fontWeight="semibold"
                                    color="fg"
                                    truncate
                                    maxW={compact ? 'full' : 'xs'}
                                    title={crumb.label || undefined}
                                    aria-current="page"
                                >
                                    {current ?? crumb.label}
                                </Text>
                            ) : !crumb.to ? (
                                <Text
                                    fontSize="sm"
                                    fontWeight="medium"
                                    color="fg.muted"
                                    truncate
                                    maxW="44"
                                    title={crumb.label}
                                >
                                    {crumb.label}
                                </Text>
                            ) : (
                                <RouterLink to={crumb.to}>
                                    <Text
                                        fontSize="sm"
                                        fontWeight="medium"
                                        color="fg.muted"
                                        truncate
                                        maxW="44"
                                        title={crumb.label}
                                        _hover={{ color: 'fg' }}
                                    >
                                        {crumb.label}
                                    </Text>
                                </RouterLink>
                            )}
                        </HStack>
                    );
                })}
            </HStack>
        </Box>
    );
}
